import { useEffect, useState } from "react";
import { appCRUD } from "../../../actions/app";
import { char } from "../../../utils";
import {
  Button, Table, Tag, Card, Row, Col,
  Statistic, Input, Select, Tabs, Badge, Tooltip, Empty
} from "antd";
import {
  EyeOutlined, PlusOutlined, SearchOutlined,
  TeamOutlined, BookOutlined, ClockCircleOutlined, UserOutlined,
  ReloadOutlined, FilterOutlined
} from "@ant-design/icons";
import { useNotification } from "../../../utils/notificationProvider";
import { EnrollmentView } from "./enrollment";
import { EnrollmentAdd } from "./add";

const { Search } = Input;
const { Option } = Select;

export const EnrollmentContent = () => {
  const [loading, setLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [open, setOpen] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [data, setData] = useState([]);
  const [enrollmentItems, setEnrollmentItems] = useState([]);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [postsPerPage, setPostsPerPage] = useState(10);
  const [nextPage, setNextPage] = useState(0);
  const [value, setValue] = useState("");
  const [dataId, setDataId] = useState(null);
  const [index, setIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("list");

  // Filters for allocation items
  const [programmeFilter, setProgrammeFilter] = useState(null);
  const [studyYearFilter, setStudyYearFilter] = useState(null);
  const [instructorFilter, setInstructorFilter] = useState(null);
  const [courseFilter, setCourseFilter] = useState(null);
  const [methodFilter, setMethodFilter] = useState(null);
  const [searchText, setSearchText] = useState("");

  const openNotification = useNotification();

  // Load enrollments list
  useEffect(() => {
    setLoading(true);
    appCRUD
      .loadEnrollment(postsPerPage, nextPage, value, "-id")
      .then((resp) => {
        setData(resp.data.results);
        setCount(resp.data.count);
        // Auto-select first enrollment if available
        if (resp.data.results?.length > 0 && !selectedEnrollment) {
          setSelectedEnrollment(resp.data.results[0]);
          setDataId(resp.data.results[0].id);
        }
      })
      .catch((error) => {
        openNotification("error", "Error", error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, postsPerPage, value, index, nextPage]);

  // Load enrollment items when enrollment is selected
  useEffect(() => {
    if (dataId) {
      setLoadingItems(true);
      appCRUD
        .loadEnrollmentItem(dataId)
        .then((resp) => {
          const items = resp.data.results || resp.data || [];
          setEnrollmentItems(Array.isArray(items) ? items : []);
        })
        .catch((error) => {
          console.error("Error loading items:", error);
          setEnrollmentItems([]);
        })
        .finally(() => {
          setLoadingItems(false);
        });
    }
  }, [dataId]);

  // Get unique programmes, years, instructors, courses, methods for filters
  const programmes = [...new Set(enrollmentItems.map(item => item.programme?.code).filter(Boolean))].sort();
  const studyYears = [...new Set(enrollmentItems.map(item => item.study).filter(Boolean))].sort();
  const courses = [...new Set(enrollmentItems.map(item => item.course).filter(Boolean))].sort();
  const instructors = [...new Set(
    enrollmentItems
      .filter(item => item.instractor_name && item.instractor_name.trim())
      .map(item => JSON.stringify({ id: item.instractor, name: item.instractor_name }))
  )].map(s => JSON.parse(s));
  const methods = [...new Set(
    enrollmentItems.flatMap(item => Array.isArray(item.teaching_method) ? item.teaching_method : []).filter(Boolean)
  )];

  // Filter items
  const filteredItems = enrollmentItems.filter(item => {
    const matchesProgramme = !programmeFilter || item.programme?.code === programmeFilter;
    const matchesYear = !studyYearFilter || item.study === studyYearFilter;
    const matchesInstructor = !instructorFilter || item.instractor === instructorFilter;
    const matchesCourse = !courseFilter || item.course === courseFilter;
    const matchesMethod = !methodFilter || (Array.isArray(item.teaching_method) && item.teaching_method.includes(methodFilter));
    const matchesSearch = !searchText ||
      item.course?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.instractor_name?.toLowerCase().includes(searchText.toLowerCase()) ||
      String(item.instractor || "").toLowerCase().includes(searchText.toLowerCase());
    return matchesProgramme && matchesYear && matchesInstructor && matchesCourse && matchesMethod && matchesSearch;
  });

  // Statistics
  const unassignedCount = enrollmentItems.filter(
    item => !item.instractor_name || !item.instractor_name.trim() || item.instractor === "-1" || item.instractor === "-2"
  ).length;
  const stats = {
    totalCourses: enrollmentItems.length,
    totalStudents: enrollmentItems.reduce((sum, item) => sum + (item.student || 0), 0),
    totalHours: enrollmentItems.reduce((sum, item) => sum + (item.hours || 0), 0),
    programmes: programmes.length,
    instructors: instructors.length,
    unassigned: unassignedCount,
  };

  // Columns for allocation items table
  const itemColumns = [
    {
      title: "#",
      key: "sn",
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Course Code",
      dataIndex: "course",
      key: "course",
      width: 120,
      sorter: (a, b) => (a.course || "").localeCompare(b.course || ""),
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Programme",
      dataIndex: ["programme", "code"],
      key: "programme",
      width: 100,
      render: (text, record) => (
        <Tag color="blue">{text || record.programme?.code || "N/A"}</Tag>
      ),
      filters: programmes.map(p => ({ text: p, value: p })),
      onFilter: (value, record) => record.programme?.code === value,
    },
    {
      title: "Year",
      dataIndex: "study",
      key: "study",
      width: 70,
      align: "center",
      render: (year) => (
        <Badge count={year} style={{ backgroundColor: getYearColor(year) }} />
      ),
      filters: studyYears.map(y => ({ text: `Year ${y}`, value: y })),
      onFilter: (value, record) => record.study === value,
    },
    {
      title: "Instructor",
      key: "instructor",
      width: 220,
      ellipsis: true,
      render: (_, record) => {
        const name = record.instractor_name;
        const id = record.instractor;
        const isUnassigned = !name || !name.trim() || id === "-1" || id === "-2";
        const displayName = isUnassigned ? "TBA" : char.toTitleCase(name);
        const tooltipContent = (
          <div>
            <div><strong>Name:</strong> {displayName}</div>
            {!isUnassigned && id && <div><strong>Employee ID:</strong> {id}</div>}
          </div>
        );
        return (
          <Tooltip title={tooltipContent}>
            <div>
              <div>
                <UserOutlined className="me-1" />
                <span>{displayName}</span>
              </div>
              {!isUnassigned && id && (
                <small className="text-muted" style={{ fontSize: "11px" }}>
                  ID: {id}
                </small>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Students",
      dataIndex: "student",
      key: "student",
      width: 90,
      align: "center",
      sorter: (a, b) => (a.student || 0) - (b.student || 0),
      render: (num) => (
        <span><TeamOutlined className="me-1" />{num || 0}</span>
      ),
    },
    {
      title: "Hours/Week",
      dataIndex: "hours",
      key: "hours",
      width: 100,
      align: "center",
      render: (hours, record) => {
        const teachingHour = record.teaching_hour;
        let display = hours || 0;
        if (Array.isArray(teachingHour)) {
          display = teachingHour.join(" + ");
        } else if (teachingHour?.hourDivision) {
          display = teachingHour.hourDivision;
        }
        return (
          <Tooltip title={`Total: ${hours || 0} hours`}>
            <Tag color="purple"><ClockCircleOutlined className="me-1" />{display}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Teaching Method",
      dataIndex: "teaching_method",
      key: "teaching_method",
      width: 150,
      render: (methods) => {
        if (!methods || !Array.isArray(methods)) return <Tag>LECTURE</Tag>;
        return methods.map((method, idx) => (
          <Tag key={idx} color={getMethodColor(method)}>{method}</Tag>
        ));
      },
    },
  ];

  // Columns for enrollment list
  const enrollmentColumns = [
    {
      key: "sn",
      title: "#",
      width: 50,
      render: (_, __, idx) => idx + 1,
    },
    {
      key: "campas",
      title: "Campus",
      dataIndex: "cumpas",
      render: (campus) => char.toTitleCase(campus?.name || "N/A"),
    },
    {
      key: "academic",
      title: "Academic Year",
      dataIndex: "academic",
      render: (academic) => academic?.name || "N/A",
    },
    {
      key: "semister",
      title: "Semester",
      dataIndex: "semister",
      render: (sem) => char.toTitleCase(sem?.name || "N/A"),
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "is_active",
      width: 100,
      render: (active) => (
        <Tag color={active ? "success" : "error"}>
          {active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (record) => (
        <Button
          type={selectedEnrollment?.id === record.id ? "primary" : "default"}
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedEnrollment(record);
            setDataId(record.id);
            setActiveTab("allocations");
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const tabItems = [
    {
      key: "list",
      label: (
        <span>
          <BookOutlined /> Enrollments
        </span>
      ),
      children: (
        <Card>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Search
              placeholder="Search enrollments..."
              allowClear
              onChange={(e) => setValue(e.target.value)}
              style={{ width: 300 }}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setOpen(true)}
            >
              New Enrollment
            </Button>
          </div>
          <Table
            loading={loading}
            columns={enrollmentColumns}
            dataSource={data}
            rowKey="id"
            pagination={{
              current: page,
              pageSize: postsPerPage,
              total: count,
              onChange: (p, size) => {
                setPage(p);
                setPostsPerPage(size);
                setNextPage((p - 1) * size);
              },
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} enrollments`,
            }}
            size="small"
            bordered
          />
        </Card>
      ),
    },
    {
      key: "allocations",
      label: (
        <span>
          <TeamOutlined /> Course Allocations
          {selectedEnrollment && (
            <Badge count={enrollmentItems.length} className="ms-2" />
          )}
        </span>
      ),
      children: selectedEnrollment ? (
        <>
          {/* Statistics Cards */}
          <Row gutter={16} className="mb-4">
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="Total Courses"
                  value={stats.totalCourses}
                  prefix={<BookOutlined />}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="Total Students"
                  value={stats.totalStudents}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="Total Hours/Week"
                  value={stats.totalHours}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: "#722ed1" }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="Programmes"
                  value={stats.programmes}
                  prefix={<FilterOutlined />}
                  valueStyle={{ color: "#fa8c16" }}
                />
              </Card>
            </Col>
          </Row>
          <Row gutter={16} className="mb-4">
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="Instructors Assigned"
                  value={stats.instructors}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: "#13c2c2" }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="Unassigned (TBA)"
                  value={stats.unassigned}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: stats.unassigned > 0 ? "#ff4d4f" : "#52c41a" }}
                />
              </Card>
            </Col>
          </Row>

          {/* Current Enrollment Info */}
          <Card className="mb-3" size="small">
            <Row>
              <Col span={8}>
                <strong>Campus:</strong> {char.toTitleCase(selectedEnrollment.cumpas?.name || "N/A")}
              </Col>
              <Col span={8}>
                <strong>Academic Year:</strong> {selectedEnrollment.academic?.name || "N/A"}
              </Col>
              <Col span={8}>
                <strong>Semester:</strong> {char.toTitleCase(selectedEnrollment.semister?.name || "N/A")}
              </Col>
            </Row>
          </Card>

          {/* Filters */}
          <Card className="mb-3" size="small">
            <Row gutter={[12, 12]}>
              <Col xs={24} md={8}>
                <Search
                  placeholder="Search course, instructor, or ID..."
                  allowClear
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  prefix={<SearchOutlined />}
                />
              </Col>
              <Col xs={12} md={4}>
                <Select
                  placeholder="Programme"
                  allowClear showSearch
                  style={{ width: "100%" }}
                  onChange={setProgrammeFilter}
                  value={programmeFilter}
                  optionFilterProp="children"
                >
                  {programmes.map(p => <Option key={p} value={p}>{p}</Option>)}
                </Select>
              </Col>
              <Col xs={12} md={3}>
                <Select
                  placeholder="Year"
                  allowClear
                  style={{ width: "100%" }}
                  onChange={setStudyYearFilter}
                  value={studyYearFilter}
                >
                  {studyYears.map(y => <Option key={y} value={y}>Year {y}</Option>)}
                </Select>
              </Col>
              <Col xs={12} md={5}>
                <Select
                  placeholder="Instructor"
                  allowClear showSearch
                  style={{ width: "100%" }}
                  onChange={setInstructorFilter}
                  value={instructorFilter}
                  optionFilterProp="children"
                >
                  {instructors.map(i => (
                    <Option key={i.id} value={i.id}>
                      {char.toTitleCase(i.name)} {i.id && `(${i.id})`}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={12} md={2}>
                <Select
                  placeholder="Method"
                  allowClear
                  style={{ width: "100%" }}
                  onChange={setMethodFilter}
                  value={methodFilter}
                >
                  {methods.map(m => <Option key={m} value={m}>{m}</Option>)}
                </Select>
              </Col>
              <Col xs={24} md={2}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setProgrammeFilter(null);
                    setStudyYearFilter(null);
                    setInstructorFilter(null);
                    setCourseFilter(null);
                    setMethodFilter(null);
                    setSearchText("");
                  }}
                  block
                >
                  Clear
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Allocations Table */}
          <Card>
            <Table
              loading={loadingItems}
              columns={itemColumns}
              dataSource={filteredItems}
              rowKey={(record, idx) => `${record.course}-${idx}`}
              pagination={{
                pageSize: 15,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} courses`,
              }}
              size="small"
              bordered
              scroll={{ x: 1000 }}
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={5}>
                      <strong>Total ({filteredItems.length} courses)</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="center">
                      <strong>{filteredItems.reduce((sum, item) => sum + (item.student || 0), 0)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="center">
                      <strong>{filteredItems.reduce((sum, item) => sum + (item.hours || 0), 0)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </Card>
        </>
      ) : (
        <Card>
          <Empty description="Select an enrollment from the list to view allocations" />
        </Card>
      ),
    },
  ];

  return (
    <>
      <EnrollmentView
        open={openView}
        setOpen={setOpenView}
        index={index}
        dataId={dataId}
      />
      <EnrollmentAdd
        open={open}
        setOpen={setOpen}
        index={index}
        setIndex={setIndex}
      />

      <div className="container-fluid">
        <div className="row mb-2">
          <div className="col-12">
            <h5 className="fw-bold text-dark mb-0">
              <BookOutlined className="me-2" />
              Course Allocations Management
            </h5>
            <p className="text-muted small mb-0">
              Manage course allocations, instructors, and teaching schedules
            </p>
          </div>
          <hr className="mt-3" />
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          type="card"
        />
      </div>
    </>
  );
};

// Helper functions
function getYearColor(year) {
  const colors = {
    1: "#52c41a",
    2: "#1890ff",
    3: "#722ed1",
    4: "#fa8c16",
  };
  return colors[year] || "#666";
}

function getMethodColor(method) {
  const colors = {
    LECTURE: "blue",
    LAB: "green",
    TUTORIAL: "orange",
    SEMINAR: "purple",
    WORKSHOP: "cyan",
  };
  return colors[method] || "default";
}
